import { useEffect, useMemo, useState } from 'react';
import {
  fetchLocationSummary,
  buildLocationSummaryPayload,
  LocationSummaryDataItem,
  LocationSummaryResponse,
  AppliedFiltersForLocationSummary,
} from '../services/onTimeDispatchLocationSummaryApi';

export interface UseLocationSummaryApiReturn {
  data: LocationSummaryDataItem[];
  totals: LocationSummaryDataItem | null;
  loading: boolean;
  error: Error | null;
  totalElements: number;
  totalPages: number;
}

/**
 * Hook to fetch Location Summary data from the API.
 * Triggers API call when applied filters change and tab is active.
 * 
 * ✅ Fixed: Now allows empty grainColumns - defaults to ['region'] in request builder
 * ✅ Fixed: Now includes sort parameters to trigger API calls on sort
 */
export const useLocationSummaryApi = (
  appliedFilters: AppliedFiltersForLocationSummary,
  isActive: boolean,
  adminLocations: any[],
  page: number = 1,
  size: number = 25,
  grainColumns: string[] = [],
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): UseLocationSummaryApiReturn => {
  const [data, setData] = useState<LocationSummaryDataItem[]>([]);
  const [totals, setTotals] = useState<LocationSummaryDataItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Build request payload from filters
  const payload = useMemo(
    () => buildLocationSummaryPayload(appliedFilters, adminLocations, page, size, grainColumns, sortBy, sortDirection),
    [appliedFilters, adminLocations, page, size, grainColumns, sortBy, sortDirection]
  );

  // Trigger API call when filters change and tab is active
  useEffect(() => {
    // ✅ Don't call if tab is not active
    // Allow empty grainColumns - will default to ['region'] in request builder
    if (!isActive) {
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[LocationSummaryApi Hook] Fetching with payload:', payload);
        const response = await fetchLocationSummary(payload);
        if (isMounted) {
          setData(response.data?.content || []);
          setTotals(response.data?.totals || null);
          setTotalElements(response.data?.totalElements || 0);
          setTotalPages(response.data?.totalPages || 0);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('[LocationSummaryApi Hook] Error:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
          setData([]);
          setTotals(null);
          setTotalElements(0);
          setTotalPages(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [payload, isActive]);

  return {
    data,
    totals,
    loading,
    error,
    totalElements,
    totalPages,
  };
};
